import React, { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import CultureHubTable, { TABLE_MIN_WIDTHS } from "./CultureHubTable";
import { pluralize } from "../../../utils/a11y/ptBr";

const CultureHubGroup = ({
  group,
  selectedIds,
  onToggleAttachment,
  onToggleGroup,
  onDeleteAssessment,
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const domId = `culturehub-group-${String(group.id).replace(/\s+/g, "-")}`;
  const headingId = `${domId}-heading`;
  const contentId = `${domId}-content`;

  const attachmentIds = useMemo(
    () =>
      group.sections.flatMap((section) =>
        section.attachments.map((attachment) => attachment.id)
      ),
    [group]
  );

  const selectedCount = attachmentIds.filter((attachmentId) =>
    selectedIds.includes(attachmentId)
  ).length;

  const isSelected =
    attachmentIds.length > 0 &&
    selectedCount === attachmentIds.length;

  const isIndeterminate =
    selectedCount > 0 &&
    selectedCount < attachmentIds.length;

  const contentMinWidth = Math.max(
    0,
    ...group.sections.map(
      (section) => TABLE_MIN_WIDTHS[section.kind] ?? 0
    )
  );

  const countLabel =
    group.kind === "Chapter"
      ? `${group.count} ${pluralize(group.count, "capítulo", "capítulos")}`
      : group.kind === "Episode"
      ? `${group.count} ${pluralize(group.count, "episódio", "episódios")}`
      : group.kind === "Addendum"
      ? `${group.count} ${pluralize(group.count, "adendo", "adendos")}`
      : group.kind === "OtherType"
      ? `${group.count} ${pluralize(group.count, "material", "materiais")}`
      : "";

  return (
    <Box
      sx={{
        border: "1px solid #DDDDDD",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          minHeight: "46px",
          px: 1.5,
          display: "grid",
          gridTemplateColumns: "48px auto 1fr 40px",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Checkbox
            size="small"
            checked={isSelected}
            indeterminate={isIndeterminate}
            onChange={() => onToggleGroup(attachmentIds)}
            // MUI only sets data-indeterminate; the native flag makes AT announce "mixed"
            inputRef={(input) => {
              if (input) input.indeterminate = isIndeterminate;
            }}
            inputProps={{
              "aria-label": `Selecionar todos os itens de ${group.label}`,
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2.5,
          }}
        >
          <Typography
            component="h3"
            id={headingId}
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              color: "#646464",
            }}
          >
            {group.label}
          </Typography>

          {!isOpen && (
            <>
              <Typography sx={countStyle}>
                {countLabel}
              </Typography>

              <Typography sx={countStyle}>
                Parecer ({group.assessmentCount ?? 0})
              </Typography>
            </>
          )}
        </Box>

        <Box />

        <IconButton
          size="small"
          onClick={() => setIsOpen((current) => !current)}
          aria-label={`Exibir itens de ${group.label}`}
          aria-expanded={isOpen}
          aria-controls={contentId}
          // grid stretches it to the 40px column width, which turns the round hover/focus shape into an oval
          sx={{ justifySelf: "center" }}
        >
          {isOpen ? (
            <KeyboardArrowUpIcon fontSize="small" />
          ) : (
            <KeyboardArrowDownIcon fontSize="small" />
          )}
        </IconButton>
      </Box>

      <Collapse
        in={isOpen}
        id={contentId}
        role="region"
        aria-labelledby={headingId}
      >
        <Box sx={{ overflowX: "auto" }}>
          <Box sx={{ minWidth: contentMinWidth }}>
            {group.sections.map((section) => (
              <CultureHubTable
                key={section.id}
                section={section}
                groupLabel={group.label}
                selectedIds={selectedIds}
                onToggleAttachment={onToggleAttachment}
                onDeleteAssessment={onDeleteAssessment}
              />
            ))}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

const countStyle = {
  fontSize: "10px",
  color: "#8E8E8E",
  textTransform: "uppercase",
};

export default CultureHubGroup;